import type {
  CalculatorConfig,
  FieldConfig,
  FieldValues,
  IrsLimitsSet,
  StepConfig,
  StepItem,
  ValidationContext,
} from '../types';

/**
 * Pure state machine driving every calculator. No React, no DOM — fully
 * unit-testable in isolation. See docs/calculator-engine-architecture.md §4.4/§4.7.
 */

export interface EngineState<TValues extends FieldValues> {
  values: TValues;
  touched: Partial<Record<keyof TValues, boolean>>;
  /** Keyed by step id (or '__intro__'). */
  submitAttempted: Record<string, boolean>;
  phase: 'intro' | 'steps' | 'results';
  stepIndex: number;
}

export type EngineAction<TValues extends FieldValues> =
  | { type: 'setValue'; name: keyof TValues; value: TValues[keyof TValues] }
  | { type: 'blur'; name: keyof TValues }
  | { type: 'attemptAdvance' }
  | { type: 'advance' }
  | { type: 'back' }
  | { type: 'goToStep'; stepId: string }
  | { type: 'showResults' }
  | { type: 'reset' };

export interface EngineContext<TValues extends FieldValues, TResult> {
  config: CalculatorConfig<TValues, TResult>;
  limits: IrsLimitsSet;
}

export function createInitialState<TValues extends FieldValues, TResult>(
  config: CalculatorConfig<TValues, TResult>,
): EngineState<TValues> {
  return {
    values: config.initialValues,
    touched: {},
    submitAttempted: {},
    phase: config.intro ? 'intro' : 'steps',
    stepIndex: 0,
  };
}

export function getVisibleSteps<TValues extends FieldValues>(
  steps: StepConfig<TValues>[],
  values: TValues,
): StepConfig<TValues>[] {
  return steps.filter((s) => !s.visibleIf || s.visibleIf(values));
}

export function getFieldsFromItems<TValues extends FieldValues>(
  items: StepItem<TValues>[],
): FieldConfig<TValues>[] {
  return items.filter((item): item is FieldConfig<TValues> => item.kind === 'field');
}

export function getAllFields<TValues extends FieldValues, TResult>(
  config: CalculatorConfig<TValues, TResult>,
): FieldConfig<TValues>[] {
  const introFields = getFieldsFromItems(config.intro?.fields ?? []);
  const stepFields = config.steps.flatMap((s) => getFieldsFromItems(s.items));
  return [...introFields, ...stepFields];
}

/**
 * A hidden field (visibleIf === false) has no effective error and is
 * excluded from validation — but its value is retained, not cleared. Only
 * an explicit `resetWhen` clears a value. See §4.3.
 */
export function getFieldError<TValues extends FieldValues>(
  field: FieldConfig<TValues>,
  values: TValues,
  ctx: ValidationContext,
): string | null {
  if (field.visibleIf && !field.visibleIf(values, ctx)) return null;

  const value = values[field.name];
  const required = field.required !== false;
  if (required && (value === null || value === undefined || value === '')) {
    return 'This field is required.';
  }
  if (field.validate) {
    const err = field.validate(value, values, ctx);
    if (err) return err;
  }
  return null;
}

/** Field-level errors for a set of fields, plus any cross-field step errors. */
export function getFieldsErrors<TValues extends FieldValues>(
  fields: FieldConfig<TValues>[],
  values: TValues,
  ctx: ValidationContext,
  crossValidate?: (v: TValues, ctx: ValidationContext) => Partial<Record<keyof TValues, string>> | null,
): Partial<Record<keyof TValues, string>> {
  const errors: Partial<Record<keyof TValues, string>> = {};
  for (const field of fields) {
    const err = getFieldError(field, values, ctx);
    if (err) errors[field.name as keyof TValues] = err;
  }
  if (crossValidate) {
    const crossErrors = crossValidate(values, ctx);
    if (crossErrors) {
      for (const key of Object.keys(crossErrors) as (keyof TValues)[]) {
        if (crossErrors[key]) errors[key] = crossErrors[key];
      }
    }
  }
  return errors;
}

export function getStepErrors<TValues extends FieldValues>(
  step: StepConfig<TValues>,
  values: TValues,
  ctx: ValidationContext,
): Partial<Record<keyof TValues, string>> {
  return getFieldsErrors(getFieldsFromItems(step.items), values, ctx, step.validate);
}

function applyResetWhen<TValues extends FieldValues, TResult>(
  values: TValues,
  config: CalculatorConfig<TValues, TResult>,
): TValues {
  let next = values;
  for (const field of getAllFields(config)) {
    if (field.resetWhen && field.resetWhen(next)) {
      const initial = config.initialValues[field.name];
      if (next[field.name] !== initial) {
        next = { ...next, [field.name]: initial };
      }
    }
  }
  return next;
}

export function calculatorReducer<TValues extends FieldValues, TResult>(
  state: EngineState<TValues>,
  action: EngineAction<TValues>,
  ctx: EngineContext<TValues, TResult>,
): EngineState<TValues> {
  const { config, limits } = ctx;
  const vctx: ValidationContext = { limits, values: state.values };

  switch (action.type) {
    case 'setValue': {
      const values = applyResetWhen({ ...state.values, [action.name]: action.value }, config);
      return { ...state, values };
    }

    case 'blur':
      return { ...state, touched: { ...state.touched, [action.name]: true } };

    case 'attemptAdvance': {
      if (state.phase === 'intro') {
        const fields = getFieldsFromItems(config.intro?.fields ?? []);
        const errors = getFieldsErrors(fields, state.values, vctx);
        if (Object.keys(errors).length > 0) {
          return { ...state, submitAttempted: { ...state.submitAttempted, __intro__: true } };
        }
        return calculatorReducer(state, { type: 'advance' }, ctx);
      }

      if (state.phase === 'steps') {
        const visibleSteps = getVisibleSteps(config.steps, state.values);
        const step = visibleSteps[state.stepIndex];
        if (!step) return state;
        const errors = getStepErrors(step, state.values, vctx);
        if (Object.keys(errors).length > 0) {
          return { ...state, submitAttempted: { ...state.submitAttempted, [step.id]: true } };
        }
        return calculatorReducer(state, { type: 'advance' }, ctx);
      }

      return state;
    }

    case 'advance': {
      if (state.phase === 'intro') {
        return { ...state, phase: 'steps', stepIndex: 0 };
      }
      if (state.phase === 'steps') {
        const visibleSteps = getVisibleSteps(config.steps, state.values);
        const isLast = state.stepIndex >= visibleSteps.length - 1;
        if (isLast) {
          return { ...state, phase: 'results' };
        }
        return { ...state, stepIndex: state.stepIndex + 1 };
      }
      return state;
    }

    case 'back': {
      if (state.phase === 'results') {
        const visibleSteps = getVisibleSteps(config.steps, state.values);
        return { ...state, phase: 'steps', stepIndex: Math.max(0, visibleSteps.length - 1) };
      }
      if (state.phase === 'steps') {
        if (state.stepIndex > 0) {
          return { ...state, stepIndex: state.stepIndex - 1 };
        }
        if (config.intro) {
          return { ...state, phase: 'intro' };
        }
      }
      return state;
    }

    case 'goToStep': {
      const visibleSteps = getVisibleSteps(config.steps, state.values);
      const idx = visibleSteps.findIndex((s) => s.id === action.stepId);
      if (idx === -1) return state;
      return { ...state, phase: 'steps', stepIndex: idx };
    }

    case 'showResults':
      return { ...state, phase: 'results' };

    case 'reset':
      return createInitialState(config);

    default:
      return state;
  }
}
